/**
 * Farm changes must stay in sequence with the policy's other changes (#37762).
 *
 * Why: farm segments after the first are stored as id-matched deltas, and each one is replayed on top
 * of the state before it. A delta is written against the state that existed when it was made, so it
 * goes wrong when that state changes underneath it. On 100058970-farm-010-CF, a change dated 10/04
 * under a GO-imported 10/30 segment:
 *   - treated a location the 10/30 segment added as a brand-new location, re-pulling 360Value and
 *     applying no coverage defaults;
 *   - deleted a risk that the 10/30 delta still modified, so the replay re-materialized it as a
 *     partial risk;
 * and ClarionDoor rejected the result ("Supplied direct input must be a float").
 *
 * Non-farm policies re-derive their later segments from change intents (#32764), so they are not
 * affected. Rather than build that rebase for farm deltas, the platform blocks what it would
 * be needed for:
 *   - a NEW change dated before the latest current-term change (`before-latest-change`). A change
 *     dated ON the latest change's date amends that change and is allowed;
 *   - an EDIT whose date moves across a neighbouring change (`moves-before-earlier-change`,
 *     `moves-past-later-change`), which reorders the replay;
 *   - an EDIT of a change that has a later change after it which adds or removes a location or risk
 *     (`adds-or-removes-under-later-change`). A removal is the partial-risk failure above; an addition
 *     is blocked too, since the later delta was written without it. A data-only edit (coverage, limits,
 *     details on existing locations and risks) of an earlier change, inception included, is allowed:
 *     the later delta only overrides the fields it changed.
 * Every date involved must be a readable `yyyy-MM-dd` calendar day; anything else blocks the change
 * (`unreadable-date`) rather than letting a string comparison wave it through.
 *
 * "Change" means a current-term segment after inception. A Renewal Change snapshot (starting after the
 * term end) is excluded, as it is from the current-term timeline everywhere else. Enforced in policy-mod
 * `validateEvent` for `previewPolicyChangeAddSegment` and `previewPolicyChangeToSegment`, and mirrored by
 * staff-fe's change date picker and submit check. Cancellation, reinstatement and rescind run through
 * their own events and are not affected.
 */
import { isFarmBasedPolicyType } from '@ourbranch/policy-types';

import { currentTermSegmentIndexes } from './current-term-segment-indexes';

export const FARM_CHANGE_OUT_OF_SEQUENCE_ERROR_CODE = 'FARM_CHANGE_OUT_OF_SEQUENCE';

export type FarmChangeSequenceReason =
  | 'before-latest-change'
  | 'moves-before-earlier-change'
  | 'moves-past-later-change'
  | 'adds-or-removes-under-later-change'
  | 'unreadable-date';

export type FarmChangeSequenceCheck =
  | { blocked: false }
  | { blocked: true; reason: FarmChangeSequenceReason; message: string };

type FarmChangeSequencePolicy = {
  policyType?: string | null;
  endDate: string;
  segments?: Array<{ segmentId?: string | null; startDate: string }> | null;
};

type FarmRiskLike = { id?: string | null; isDeleted?: boolean | null } | null;
type FarmLocationLike = { id?: string | null; isDeleted?: boolean | null; risks?: FarmRiskLike[] | null } | null;
type FarmDetailsLike = { farmLocations?: FarmLocationLike[] | null } | null | undefined;

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** The `yyyy-MM-dd` calendar day of `value` (a date or an ISO timestamp), or undefined when unreadable. */
function readIsoDay(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const day = value.slice(0, 10);
  const match = ISO_DAY.exec(day);
  if (!match || (value.length > 10 && value[10] !== 'T')) {
    return undefined;
  }
  const [, year, month, date] = match.map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, date));
  const isCalendarDay =
    parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === date;
  return isCalendarDay ? day : undefined;
}

const formatUsDate = (isoDay: string) => {
  const [year, month, day] = isoDay.split('-');
  return `${month}/${day}/${year}`;
};

const blocked = (reason: FarmChangeSequenceReason, message: string): FarmChangeSequenceCheck => ({
  blocked: true,
  reason,
  message
});

const unreadable = (value: unknown) =>
  blocked(
    'unreadable-date',
    `This change can't be checked against the policy's other changes because the date ${JSON.stringify(value ?? null)} ` +
      "isn't a valid date (expected yyyy-MM-dd). Choose the change date again; if this keeps happening, escalate to development."
  );

/** The current-term segments after inception, in array order, with their index. */
function currentTermChanges(policy: FarmChangeSequencePolicy) {
  const segments = policy.segments ?? [];
  return currentTermSegmentIndexes({ endDate: policy.endDate, segments })
    .filter((index) => index > 0)
    .map((index) => ({ index, segment: segments[index] }));
}

/**
 * The earliest date a NEW farm change may carry: the start date (yyyy-MM-dd) of the latest current-term
 * segment after inception. Undefined for a non-farm policy, a policy with no such segment, or one whose
 * change dates are unreadable (the submit check then blocks with `unreadable-date`).
 */
export function farmChangeDateFloor({ policy }: { policy: FarmChangeSequencePolicy }): string | undefined {
  if (!isFarmBasedPolicyType(policy.policyType)) {
    return undefined;
  }
  let floor: string | undefined;
  for (const { segment } of currentTermChanges(policy)) {
    const start = readIsoDay(segment.startDate);
    if (!start) {
      return undefined;
    }
    if (!floor || start > floor) {
      floor = start;
    }
  }
  return floor;
}

/** Location ids, and `locationId/riskId` pairs, that are live (not tombstoned) in `details`. */
function liveLocationAndRiskKeys(details: FarmDetailsLike): { keys: Set<string>; hasUnidentified: boolean } {
  const keys = new Set<string>();
  let hasUnidentified = false;
  for (const location of details?.farmLocations ?? []) {
    if (!location || location.isDeleted) {
      continue;
    }
    if (!location.id) {
      hasUnidentified = true;
      continue;
    }
    keys.add(`location:${location.id}`);
    for (const risk of location.risks ?? []) {
      if (!risk || risk.isDeleted) {
        continue;
      }
      if (!risk.id) {
        hasUnidentified = true;
        continue;
      }
      keys.add(`risk:${location.id}/${risk.id}`);
    }
  }
  return { keys, hasUnidentified };
}

/**
 * True when `incomingDetails` adds or removes a location or risk relative to `baselineDetails`. An
 * incoming payload with no `farmLocations` leaves them as they are. Fails closed: locations with no
 * baseline to compare against count as a change.
 */
function addsOrRemovesLocationsOrRisks(baselineDetails: FarmDetailsLike, incomingDetails: FarmDetailsLike): boolean {
  if (!Array.isArray(incomingDetails?.farmLocations)) {
    return false;
  }
  if (!Array.isArray(baselineDetails?.farmLocations)) {
    return true;
  }
  const before = liveLocationAndRiskKeys(baselineDetails);
  const after = liveLocationAndRiskKeys(incomingDetails);
  if (after.hasUnidentified || before.keys.size !== after.keys.size) {
    return true;
  }
  for (const key of before.keys) {
    if (!after.keys.has(key)) {
      return true;
    }
  }
  return false;
}

/**
 * Decides whether a farm change is in sequence with the policy's other changes (see the file header).
 *
 * - `requestedDate`: the change's date. For an edit, a missing date means the edited change keeps its own.
 * - `editedSegmentId`: the segment an edit targets; omit for a new change. An id that is not on the policy
 *   is judged as a new change.
 * - `baselineDetails` / `incomingDetails`: for an edit, the policy details up through the edited segment
 *   and the details the edit submits, used to spot an added or removed location or risk.
 */
export function checkFarmChangeSequence({
  policy,
  requestedDate,
  editedSegmentId,
  baselineDetails,
  incomingDetails
}: {
  policy: FarmChangeSequencePolicy;
  requestedDate: string | null | undefined;
  editedSegmentId?: string | null;
  baselineDetails?: FarmDetailsLike;
  incomingDetails?: FarmDetailsLike;
}): FarmChangeSequenceCheck {
  if (!isFarmBasedPolicyType(policy.policyType)) {
    return { blocked: false };
  }
  const segments = policy.segments ?? [];
  const editedIndex = editedSegmentId ? segments.findIndex((segment) => segment?.segmentId === editedSegmentId) : -1;
  // The other current-term changes after inception: these are what the change must stay in sequence with.
  const others = currentTermChanges(policy).filter(({ index }) => index !== editedIndex);
  if (!others.length) {
    return { blocked: false };
  }
  const otherDates: string[] = [];
  for (const { segment } of others) {
    const start = readIsoDay(segment.startDate);
    if (!start) {
      return unreadable(segment.startDate);
    }
    otherDates.push(start);
  }

  if (editedIndex < 0) {
    const requested = readIsoDay(requestedDate);
    if (!requested) {
      return unreadable(requestedDate);
    }
    const latest = otherDates.reduce((max, date) => (date > max ? date : max));
    if (requested < latest) {
      return blocked(
        'before-latest-change',
        `This change can't be dated before ${formatUsDate(latest)}, the date of the policy's latest change. ` +
          `Date it on or after ${formatUsDate(latest)}.`
      );
    }
    return { blocked: false };
  }

  const storedDateRaw = segments[editedIndex].startDate;
  const stored = readIsoDay(storedDateRaw);
  if (!stored) {
    return unreadable(storedDateRaw);
  }
  const requested = requestedDate === undefined || requestedDate === null ? stored : readIsoDay(requestedDate);
  if (!requested) {
    return unreadable(requestedDate);
  }
  const earlier = otherDates.filter((date) => date < stored);
  const later = otherDates.filter((date) => date > stored);
  const previous = earlier.length ? earlier.reduce((max, date) => (date > max ? date : max)) : undefined;
  const next = later.length ? later.reduce((min, date) => (date < min ? date : min)) : undefined;

  if (previous && requested < previous) {
    return blocked(
      'moves-before-earlier-change',
      `This change can't be moved to ${formatUsDate(requested)}, before the policy's earlier change on ` +
        `${formatUsDate(previous)}. Keep it after ${formatUsDate(previous)}.`
    );
  }
  if (next && requested > next) {
    return blocked(
      'moves-past-later-change',
      `This change can't be moved to ${formatUsDate(requested)}, past the policy's later change on ` +
        `${formatUsDate(next)}. Keep it before ${formatUsDate(next)}, or make a new change dated on or after ` +
        `${formatUsDate(next)}.`
    );
  }
  if (next && addsOrRemovesLocationsOrRisks(baselineDetails, incomingDetails)) {
    const latest = later.reduce((max, date) => (date > max ? date : max));
    return blocked(
      'adds-or-removes-under-later-change',
      `This change can't add or remove a location or risk, because the policy has a later change on ` +
        `${formatUsDate(latest)} that was built on top of it. Make that part of the change as a new change ` +
        `dated on or after ${formatUsDate(latest)}.`
    );
  }
  return { blocked: false };
}

/** Shown when a new farm change's picker range is empty because the latest change leaves no date after it. */
export function farmNoNewChangeAvailableMessage(latestChangeDate: string): string {
  const formatted = formatUsDate(latestChangeDate);
  return (
    `A new change has to be dated after the policy's latest change on ${formatted}, and no later date is ` +
    `available here. Edit the ${formatted} change instead.`
  );
}
